// Expert profiles CRUD hook
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

const EXPERTS_KEY = 'expert-profiles'

export interface ExpertProfile {
  id: string
  organization_id: string
  user_id: string | null
  full_name: string
  email: string
  expertise_domains: string[]
  d365_modules: string[]
  d365_versions: string[]
  max_concurrent_assignments: number
  availability_status: string
  is_validator: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

interface ExpertProfileInput {
  full_name: string
  email: string
  expertise_domains: string[]
  d365_modules: string[]
  d365_versions: string[]
  max_concurrent_assignments: number
  availability_status: string
  is_validator: boolean
}

export function useExpertProfiles() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const profilesQuery = useQuery({
    queryKey: [EXPERTS_KEY],
    queryFn: async (): Promise<ExpertProfile[]> => {
      const { data, error } = await supabase
        .from('expert_profiles')
        .select('*')
        .eq('is_active', true)
        .order('full_name', { ascending: true })

      if (error) throw error
      return (data ?? []) as unknown as ExpertProfile[]
    },
    enabled: !!user,
  })

  const createMutation = useMutation({
    mutationFn: async (input: ExpertProfileInput) => {
      if (!user) throw new Error('Not authenticated')
      const { error } = await supabase.from('expert_profiles').insert({
        organization_id: user.organizationId,
        ...input,
        is_active: true,
      } as never)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPERTS_KEY] }),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: ExpertProfileInput & { id: string }) => {
      const { error } = await supabase
        .from('expert_profiles')
        .update(input as never)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPERTS_KEY] }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('expert_profiles')
        .update({ is_active: false } as never)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPERTS_KEY] }),
  })

  return {
    profiles: profilesQuery.data ?? [],
    isLoading: profilesQuery.isLoading,
    error: profilesQuery.error,
    createProfile: createMutation.mutateAsync,
    updateProfile: updateMutation.mutateAsync,
    deleteProfile: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
  }
}
